class NutritionFlow extends HTMLElement {
  #animationFrame = 0;
  #mediaQuery = window.matchMedia('(max-width: 749px)');
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #mobileCards = [];
  #steps = [];
  #dots = [];

  connectedCallback() {
    this.#mobileCards = Array.from(this.querySelectorAll('.nutrition-flow__mobile-card'));
    this.#steps = Array.from(this.querySelectorAll('[data-story-step]'));
    this.#dots = Array.from(this.querySelectorAll('[data-story-dot]'));

    this.dataset.activeStep = this.dataset.activeStep || '0';
    // Enables the inactive-step dimming. Set here so the CSS default stays
    // readable when this script doesn't run.
    this.dataset.enhanced = '';
    this.#syncDots(Number(this.dataset.activeStep));

    this.addEventListener('click', this.#handleClick);
    window.addEventListener('resize', this.#queueUpdate);
    window.addEventListener('scroll', this.#queueUpdate, { passive: true });
    this.#mediaQuery.addEventListener('change', this.#queueUpdate);
    this.#reducedMotionQuery.addEventListener('change', this.#queueUpdate);

    this.#queueUpdate();
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.#animationFrame);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('resize', this.#queueUpdate);
    window.removeEventListener('scroll', this.#queueUpdate);
    this.#mediaQuery.removeEventListener('change', this.#queueUpdate);
    this.#reducedMotionQuery.removeEventListener('change', this.#queueUpdate);
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-story-dot]');
    if (button instanceof HTMLButtonElement) {
      const nextIndex = Number(button.dataset.storyDot);
      const nextStep = this.#steps[nextIndex];
      if (!(nextStep instanceof HTMLElement)) return;

      const top = window.scrollY + nextStep.getBoundingClientRect().top - window.innerHeight * 0.18;
      window.scrollTo({
        top,
        behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
      return;
    }

    const scrollPrompt = target.closest('[data-story-scroll]');
    if (!(scrollPrompt instanceof HTMLButtonElement)) return;

    const nextTarget = this.#mediaQuery.matches ? this.#mobileCards[0] : this.#steps[0];
    if (!(nextTarget instanceof HTMLElement)) return;

    const top = window.scrollY + nextTarget.getBoundingClientRect().top - window.innerHeight * 0.1;
    window.scrollTo({
      top,
      behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  };

  #queueUpdate = () => {
    if (this.#animationFrame) return;

    this.#animationFrame = window.requestAnimationFrame(() => {
      this.#animationFrame = 0;
      this.#update();
    });
  };

  #update() {
    if (this.#mediaQuery.matches) {
      if (!this.#mobileCards.length) {
        this.dataset.activeStep = '0';
        this.#syncDots(0);
        this.style.removeProperty('--nutrition-parallax');
        return;
      }

      const activeIndex = this.#getActiveIndex(this.#mobileCards);

      this.dataset.activeStep = String(activeIndex);
      this.#syncDots(activeIndex);
      this.style.removeProperty('--nutrition-parallax');
      return;
    }

    if (!this.#steps.length) {
      this.dataset.activeStep = '0';
      this.#syncDots(0);
      this.style.removeProperty('--nutrition-parallax');
      return;
    }

    const activeIndex = this.#getActiveIndex(this.#steps);

    this.dataset.activeStep = String(activeIndex);
    this.#syncDots(activeIndex);

    const rect = this.getBoundingClientRect();
    const scrollRange = Math.max(this.offsetHeight - window.innerHeight, 1);
    const progress = Math.min(Math.max(-rect.top / scrollRange, 0), 1);
    const parallaxOffset = (progress - 0.5) * 64;

    this.style.setProperty('--nutrition-parallax', `${parallaxOffset.toFixed(2)}px`);
  }

  #syncDots(activeIndex) {
    this.#dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  #getActiveIndex(elements) {
    const viewportCenter = window.innerHeight * 0.5;
    let activeIndex = 0;
    let smallestDistance = Number.POSITIVE_INFINITY;

    elements.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const distance = Math.abs(center - viewportCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }
}

if (!customElements.get('nutrition-flow')) {
  customElements.define('nutrition-flow', NutritionFlow);
}
